import { useGetLocale, useTranslate } from '@refinedev/core';
import type { CrudFilters } from '@refinedev/core';
import { Divider, Flex, Typography, theme } from 'antd';
import { useResourceCount } from '@/hooks/useResourceCount';

const { Text } = Typography;

export type Severity = 'error' | 'warning';

/** A named filter set: an alert when it carries a severity, a stat when not. */
export interface SummaryBarQuery<K extends string = string> {
  key: K;
  filters: CrudFilters;
  severity?: Severity;
}

interface CountProps {
  resource: string;
  labelPrefix: string;
  query: SummaryBarQuery;
}

/**
 * One alert as a card — rendered only when its count is > 0. Errors are the
 * filled "urgent" card; warnings are plain "review" cards.
 */
const AlertCard = ({
  resource,
  labelPrefix,
  query,
  active,
  onClick,
}: CountProps & { active: boolean; onClick: () => void }) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const { token } = theme.useToken();
  const { total } = useResourceCount(resource, query.filters);
  if (!total) return null;

  const urgent = query.severity === 'error';
  const fg = urgent ? token.colorTextLightSolid : token.colorText;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: urgent ? '1.5 1 260px' : '1 1 180px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: token.marginXXS,
        padding: token.padding,
        borderRadius: token.borderRadiusLG,
        background: urgent ? token.colorError : token.colorBgContainer,
        border: `${token.lineWidth}px solid ${
          urgent ? token.colorError : token.colorBorderSecondary
        }`,
        outline: active
          ? `${token.lineWidthFocus}px solid ${token.colorPrimary}`
          : undefined,
        outlineOffset: token.lineWidthFocus,
        cursor: 'pointer',
        font: 'inherit',
        textAlign: 'left',
        color: fg,
      }}
    >
      <span
        style={{
          fontSize: token.fontSizeSM,
          fontWeight: token.fontWeightStrong,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: urgent ? fg : token.colorWarningText,
        }}
      >
        {t(`common.summary_bar.${urgent ? 'urgent' : 'review'}`)}
      </span>
      <span
        style={{
          fontSize: token.fontSizeHeading2,
          lineHeight: token.lineHeightHeading2,
          fontWeight: token.fontWeightStrong,
        }}
      >
        {total.toLocaleString(locale)}
      </span>
      <span style={{ fontWeight: urgent ? token.fontWeightStrong : undefined }}>
        {t(`${labelPrefix}.alerts.${query.key}`)}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: token.fontSizeSM,
          color: urgent ? fg : token.colorTextSecondary,
        }}
      >
        {t(`${labelPrefix}.view_descriptions.${query.key}`)}
      </span>
      <span
        style={{
          marginTop: token.marginXXS,
          ...(urgent
            ? {
                padding: `0 ${token.paddingXS}px`,
                borderRadius: token.borderRadiusSM,
                background: token.colorTextLightSolid,
                color: token.colorError,
                fontWeight: token.fontWeightStrong,
              }
            : { color: token.colorLink }),
        }}
      >
        {t(`common.summary_bar.${urgent ? 'fix' : 'show'}`)} →
      </span>
    </button>
  );
};

/** Read-only summary figure — informational, not a filter. */
const Stat = ({ resource, labelPrefix, query }: CountProps) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const { total } = useResourceCount(resource, query.filters);
  return (
    <span>
      <Text type="secondary">{t(`${labelPrefix}.alerts.${query.key}`)}</Text>{' '}
      <Text strong>{total?.toLocaleString(locale) ?? '–'}</Text>
    </span>
  );
};

export interface SummaryAlertsBarProps<K extends string> {
  /** Resource the counts are read from. */
  resource: string;
  /** Namespace holding this bar's `alerts.*` and `view_descriptions.*`. */
  labelPrefix: string;
  /** Actionable filter sets, shown as cards while non-empty. */
  alerts: SummaryBarQuery<K>[];
  /** Figures shown above the cards; never clickable. */
  stats?: SummaryBarQuery<K>[];
  activeKey: K | null;
  /** Page-level scope applied on top of every query's own filters. */
  scope?: CrudFilters;
  /** Click toggles the query's filter set; `null` clears it. */
  onSelect: (query: SummaryBarQuery<K> | null) => void;
}

/** Quick filters: non-empty actionable alerts as cards, stats above. */
export const SummaryAlertsBar = <K extends string>({
  resource,
  labelPrefix,
  alerts,
  stats = [],
  activeKey,
  scope = [],
  onSelect,
}: SummaryAlertsBarProps<K>) => {
  const { token } = theme.useToken();
  const scoped = (q: SummaryBarQuery<K>): SummaryBarQuery<K> => ({
    ...q,
    filters: [...scope, ...q.filters],
  });
  const toggle = (q: SummaryBarQuery<K>) => () =>
    onSelect(activeKey === q.key ? null : q);

  return (
    <Flex
      vertical
      gap={token.marginSM}
      style={{ marginBottom: token.marginSM }}
    >
      {stats.length > 0 && (
        <Flex justify="flex-end" align="center" gap={token.marginSM} wrap>
          {stats.map((q, i) => (
            <Flex key={q.key} align="center" gap={token.marginSM}>
              {i > 0 && <Divider type="vertical" style={{ margin: 0 }} />}
              <Stat
                resource={resource}
                labelPrefix={labelPrefix}
                query={scoped(q)}
              />
            </Flex>
          ))}
        </Flex>
      )}
      <Flex gap={token.marginSM} wrap>
        {alerts.map((q) => (
          <AlertCard
            key={q.key}
            resource={resource}
            labelPrefix={labelPrefix}
            query={scoped(q)}
            active={activeKey === q.key}
            onClick={toggle(q)}
          />
        ))}
      </Flex>
    </Flex>
  );
};
